/**
 * Integration Marketplace Demo — in-memory fixture store.
 *
 * THROWAWAY DEMO CODE (see ./README.md). A tiny external store built on
 * `useSyncExternalStore` (no extra deps) holding the whole demo dataset at
 * module scope, so non-component helpers can read the snapshot synchronously
 * while components subscribe for reactive updates. Mirrors the pattern in
 * `frontend/src/demo/ar/fixture-store.tsx`.
 *
 *   - `getImDemoState()`            — synchronous read.
 *   - `useImDemoStore(selector)`    — reactive read in a component.
 *   - `updateImDemoState(producer)` — mutate (a flow step applying a change).
 *   - `resetImDemoState()`          — one-click reset back to the seed.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { PropsWithChildren } from 'react';
import { buildImDemoSeed } from './fixture-seed';
import type { ImDemoState } from './types';

let state: ImDemoState = buildImDemoSeed();
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Synchronous read of the current demo snapshot. */
export function getImDemoState(): ImDemoState {
  return state;
}

/**
 * Apply a mutation. The producer receives a fresh deep clone it may mutate in
 * place and/or return; whatever it returns (or the mutated clone) becomes the
 * new snapshot. Always produces a new top-level reference so subscribers
 * re-render.
 */
export function updateImDemoState(
  producer: (draft: ImDemoState) => ImDemoState | void,
): void {
  const draft = structuredClone(state);
  const next = producer(draft);
  state = next ?? draft;
  emit();
}

/** One-click reset: re-seed the store to its initial snapshot. */
export function resetImDemoState(): void {
  state = buildImDemoSeed();
  emit();
}

/** Structural equality for demo snapshots (plain JSON-like data). */
function snapshotEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, i) => snapshotEqual(item, b[i]));
  }
  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const aKeys = Object.keys(aRec);
  if (aKeys.length !== Object.keys(bRec).length) return false;
  return aKeys.every(
    (k) =>
      Object.prototype.hasOwnProperty.call(bRec, k) &&
      snapshotEqual(aRec[k], bRec[k]),
  );
}

/**
 * Reactive selector hook. Re-renders the caller when the selected slice changes
 * (by structural equality), returning a stable reference otherwise so selectors
 * that build a fresh object/array each call don't loop forever.
 */
export function useImDemoStore<T>(selector: (s: ImDemoState) => T): T {
  const cache = useRef<{ value: T } | null>(null);
  const getSnapshot = (): T => {
    const next = selector(state);
    if (cache.current && snapshotEqual(cache.current.value, next)) {
      return cache.current.value;
    }
    cache.current = { value: next };
    return next;
  };
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Optional provider — re-seeds the store on mount so each fresh entry into the
 * demo starts clean. Mounted only inside the demo wrapper so it never affects
 * production.
 */
export function ImDemoStoreProvider({ children }: PropsWithChildren) {
  useEffect(() => {
    resetImDemoState();
  }, []);
  return children;
}
